import axios from "axios";
import { APIResponse } from "../../../models/NetworkResponse";
import { getAuthorizationHeader } from "../../../api/AuthSession";
import { setupAuthInterceptor } from "../../../api/setupAuthInterceptor";

setupAuthInterceptor();

// Mirrors src/api/Networking.ts but targets Swayy Commander's own backend.
// Auth token is shared with Swageazy (same login mechanism), only the base
// URL differs.
interface Params {
  baseUrl: string;
  headers: any;
  method: string;
}

const config = (method: string, token?: string | null): Params => ({
  baseUrl: process.env.REACT_APP_SWAYY_API_URL || "",
  headers: {
    Authorization: token,
  },
  method,
});

const createQueryParams = (params?: any): string | null => {
  if (!params) return null;

  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });

  return searchParams.toString();
};

const getToken = () => getAuthorizationHeader() || null;

export const getAPI = async <T = APIResponse>(
  url: string,
  params?: any,
  signal?: AbortSignal,
): Promise<T> => {
  const query_string = createQueryParams(params);
  const { baseUrl, ...rest } = config("get", getToken());
  const response = await axios({
    ...rest,
    ...(signal && { signal }),
    url:
      query_string && query_string !== ""
        ? `${baseUrl}${url}?${query_string}`
        : `${baseUrl}${url}`,
  });
  return response.data as T;
};

export const postAPI = async (
  url: string,
  data?: any,
  signal?: AbortSignal,
): Promise<APIResponse> => {
  const { baseUrl, ...rest } = config("post", getToken());
  const response = await axios({
    ...rest,
    url: `${baseUrl}${url}`,
    data,
    ...(signal && { signal }),
  });
  return response.data as APIResponse;
};

export const putAPI = async (url: string, data?: any): Promise<APIResponse> => {
  const { baseUrl, ...rest } = config("put", getToken());
  const response = await axios({ ...rest, url: `${baseUrl}${url}`, data });
  return response.data as APIResponse;
};

export const patchAPI = async <T = APIResponse>(
  url: string,
  data?: any,
): Promise<T> => {
  const { baseUrl, ...rest } = config("patch", getToken());
  const response = await axios({ ...rest, url: `${baseUrl}${url}`, data });
  return response.data as T;
};

export const deleteAPI = async <T = APIResponse>(
  url: string,
  body?: Record<string, any>,
): Promise<T> => {
  const { baseUrl, ...rest } = config("delete", getToken());
  const response = await axios({
    ...rest,
    url: `${baseUrl}${url}`,
    ...(body && { data: body }),
  });
  return response.data as T;
};

export const postFileAPI = async (
  url: string,
  data?: any,
): Promise<{ blob: Blob; filename: string }> => {
  const { baseUrl, ...rest } = config("post", getToken());
  try {
    const response = await axios({
      ...rest,
      url: `${baseUrl}${url}`,
      data,
      responseType: "blob",
    });
    const disposition = String(response.headers["content-disposition"] || "");
    const match = disposition.match(/filename=([^;]+)/i);
    return {
      blob: response.data as Blob,
      filename: match?.[1]?.replace(/["']/g, "").trim() || "export.csv",
    };
  } catch (error: any) {
    const blob = error?.response?.data;
    if (blob instanceof Blob) {
      try {
        const parsed = JSON.parse(await blob.text()) as { message?: string };
        if (parsed.message) throw new Error(parsed.message);
      } catch (parseError) {
        if (parseError instanceof Error && parseError.message !== "Unexpected end of JSON input") {
          throw parseError;
        }
      }
    }
    throw error;
  }
};

export const postFormDataAPI = async (
  url: string,
  formData: FormData,
): Promise<APIResponse> => {
  const { baseUrl, headers } = config("post", getToken());
  const response = await axios({
    method: "post",
    url: `${baseUrl}${url}`,
    headers: {
      ...headers,
      "Content-Type": "multipart/form-data",
    },
    data: formData,
  });
  return response.data as APIResponse;
};
